<script setup lang="ts">
import { useUserStore } from "~/store/user/userStore";
import { getPolicyCookies, removeCookie, setCookie, setPolicyCookies } from "~~/package/utils/CookieUtil";
import { RouteName } from "~~/package/enum/RouteName";
import { useUserService } from "~/composables/service/useUserService";
import { computed, useRouter, watchEffect } from "#imports";
import { ManagerRouteName } from "~~/package/enum/ManagerRouteName";
import { useDialogStore } from "~~/application/store/dialog/dialogStore";
import DialogType from "~~/application/components/Common/Dialog/model/DialogType";
import { useNuxtApp } from "#app";
import { ToastType } from "~/components/Common/Toast/model/ToastType";
import { useToastStore } from "~/store/toast/toastStore";
import { useThirdPartyRegistrationService } from "~/composables/service/useThirdPartyRegistrationService";
import { CookieKeyType } from "~~/package/mapper/common/model/CookieKeyType";
import { SignType, SignUIType } from "~/components/Sign/enum/SignType";
import { useUtilsStore } from "~/store/utils/utilsStore";
import { applicationContext } from "@@/package/application/applicationContext";
import { resolveConsentAction, buildAutoAcceptPolicy, afterCountryLookup } from "~/utils/cookieConsent";
import { sessionOwnerScope } from "~/composables/useSessionOwner";
import { isRenewalRouteName } from "~~/package/config/renewalRoutes";
import { useRenewalLocale } from "~~/codepresso-renewal/composables/useRenewalText";

const { t } = useI18n();
const { $i18n } = useNuxtApp();
const route = useRoute();
const router = useRouter();

const toastStore = useToastStore();
const userStore = useUserStore();
const userService = useUserService();
const utilsStore = useUtilsStore();
const { guard } = usePermissionGuard();

const isReady = ref(false);
const locale = ref("en");

// renewal 콘텐츠와 GNB 의 언어를 우리 언어에 맞춘다.
// 루트에서 거는 이유: 헤더(GNB)는 페이지 콘텐츠보다 먼저 렌더된다. 콘텐츠 래퍼에서
// 걸면 헤더가 이미 기본 언어로 그려진 뒤라, SSR 출력의 GNB 만 언어가 어긋난다.
// (그 상태는 hydration 에서 교정되지 않는다 — v-html 의 innerHTML 은 다시 쓰이지 않는다)
const { setRenewalLocale } = useRenewalLocale();
watchEffect(() => setRenewalLocale($i18n.locale.value));
const thirdPartyRegistrationService = useThirdPartyRegistrationService();
const { onSubscriptionUpdate } = useSubscriptionBroadcast();
const env = applicationContext.getConfig().ENVIRONMENT;
const timer = ref<string | number | NodeJS.Timeout | null | undefined>(null);

onMounted(() => {
  initRouterHook();
  getUserData();

  thirdPartyRegistrationService.registration();

  onSubscriptionUpdate((success) => {
    if (success && !userStore.isLogOut()) {
      userService.setUser(false);
    }
  });
});

onBeforeUnmount(() => {
  removeCookie(CookieKeyType.REDIRECT_URL);
});

watch(
  () => [$i18n.locale, userStore.userV2.uuid],
  () => {
    switch (route.name) {
      case RouteName.Home:
      case RouteName.Sign:
      case RouteName.AuthUserPasswordChange:
      case RouteName.AuthUserEmail:
      case RouteName.AuthUserSocial:
        return;
      default:
        locale.value = $i18n.locale.value;
    }
  },
  { immediate: true, deep: true }
);

// TSK-11215: identifies "the same screen" for the unique-tab election. Used for both the
// guard's :key and its scope prop so the two can never disagree.
const sessionScope = computed(() => sessionOwnerScope(route.name, route.params));

const unnecessaryLoginRouterName = computed(() => [RouteName.Sign, RouteName.Auth]);
const unlimitedRouterName = computed(() => [
  RouteName.Profile,
  RouteName.Policy,
  RouteName.Home,
  RouteName.Root,
  RouteName.Test,
  RouteName.ErrorCode,
  RouteName.Catchall,
]);

// Anonymous-only marketing routes that can render before `getUserData()` resolves.
// Narrower than the prior `isPublicRoute` which also included Sign / Auth and
// broke Google OAuth: the OAuth callback handler on /sign reads userStore /
// router hooks that only exist after `getUserData()` finishes, so /sign must
// stay behind the `isReady` gate. Profile and Test are also intentionally
// excluded (auth-required / debug).
const isAnonMarketingRoute = computed(() => {
  if (typeof route.name !== "string") return false;
  // renewal 콘텐츠로 서빙하는 로그인 이전 페이지도 익명 마케팅 라우트다.
  // 이름 전체로 비교한다 — 아래 접두 비교는 `ax-build` 를 `ax` 로 잘라 버린다.
  if (isRenewalRouteName(route.name)) return true;
  const top = route.name.split("-")[0] as RouteName;
  return top === RouteName.Home || top === RouteName.Root || top === RouteName.Policy;
});

const isOrgInvitationPath = () => {
  return route.path === "/auth/org/email" || route.path.startsWith("/auth/org/email");
};

const isUnnecessaryLoginRouter = (routerName: RouteName) => {
  return unnecessaryLoginRouterName.value.includes(routerName);
};

const isRouteNameOnlyManagerTypeCanAssess = (RouterName: ManagerRouteName) => {
  return Object.values(ManagerRouteName).includes(RouterName);
};

const isManagerType = computed(() => {
  return (
    userStore.userV2.orgInfo !== null && (userStore.isAdminType || userStore.isSuperAdminType)
  );
});

const parseBoolean = (v: unknown, defaultValue = true) => {
  if (v === undefined || v === null) return defaultValue;
  if (typeof v === "boolean") return v;
  const s = String(v).toLowerCase().trim();
  if (["false", "0", "no", "n"].includes(s)) return false;
  if (["true", "1", "yes", "y"].includes(s)) return true;
  return defaultValue;
};

const closePopups = () => {
  useDialogStore().close();
};

const stayHere = async () => {
  await router.replace({
    path: route.path,
    query: { ...route.query },
    params: { ...route.params }
  });
};

const setRedirectCookie = () => {
  setCookie(
    CookieKeyType.REDIRECT_URL,
    {
      fullPath: route.fullPath,
      path: route.path,
      query: route.query,
      params: route.params,
      isManager: isRouteNameOnlyManagerTypeCanAssess(route.name as ManagerRouteName)
    }, {
      expires: new Date(Date.now() + 86400 * 1000),
      maxAge: 86400
    }
  );
};

const setRegisterContextCookie = () => {
  setCookie(
    CookieKeyType.AUTH_ORG_EMAIL_SIGNUP_CONTEXT,
    {
      from: "auth-org-email",
      createdAt: Date.now(),
    },{
      expires: new Date(Date.now() + 86400 * 1000),
      maxAge: 86400
    }
  );
}

const redirectToSignWithToast = async (isRegistered: boolean, signType: SignType) => {
  toastStore.openToast({
    toastType: ToastType.WARNING,
    contents: isRegistered ? t("account-required") : t("account-up-required"),
  });

  setRedirectCookie();

  await router.replace({
    path: "/sign",
    query: {
      uiType: isRegistered ? SignUIType.LOGIN : SignUIType.SIGNUP,
      type: signType,
      userEmail: route.query.userEmail
    },
  });
};

const getTopRouteName = (): RouteName => {
  if (!route.name || typeof route.name !== "string") return RouteName.Root;
  return (route.name.split("-")[0] as RouteName);
};

const initRouterHook = () => {
  router.beforeEach((to, from) => {
    timer.value && clearTimeout(timer.value);
    closePopups();
  });

  router.afterEach((to, from) => {
    if (userStore.xCountry === "") {
      // 조회 실패(차단/오프라인/5xx)에도 판정은 실행 — xCountry=''는 판정 불가 → 팝업(A안)
      afterCountryLookup(userService.setXCountry(), setCookiePopupTimer);
    } else {
      setCookiePopupTimer();
    }
  });
};

const setCookiePopupTimer = () => {
  timer.value = setTimeout(() => {
    const policy = getPolicyCookies();
    const action = resolveConsentAction(policy, userStore.xCountry ?? '');

    if (action === 'popup') {
      openCookieAcceptDialog();
    } else if (action === 'auto-accept') {
      const autoAccepted = buildAutoAcceptPolicy(policy);
      setPolicyCookies(autoAccepted);
      // 로그인 상태로 재방문한 기존 사용자의 채널톡이 리로드 없이 뜨도록 신호 갱신
      utilsStore.functionalConsent = autoAccepted.functional ?? null;
    }

    timer.value = null;
  }, 500);
};

const openCookieAcceptDialog = () => {
  useDialogStore().open({
    type: DialogType.CookieAccept,
    props: {}
  });
};

const getUserData = async () => {
  // SEC-1 (TSK-10568): JS can't read the HttpOnly AccessToken cookie, so we can't
  // pre-check session presence. Always attempt setUser; backend returns user data
  // if the cookie (or legacy Bearer header) is valid, 401 otherwise. The 401
  // interceptor in HttpClient handles cleanup + redirect.
  try {
    try {
      await userService.setUser(false);
    } catch (e) {
      removeCookie(CookieKeyType.ACCESS_TOKEN); // legacy JS-cookie cleanup
      removeCookie(CookieKeyType.OPEN_CREDIT_BANNER);
      // Don't force-redirect here; the global 401 interceptor handles auth-required pages,
      // and unauthenticated visitors landing on a public page should remain there.
    }
    await userAuth(userStore.userV2.uuid);
  } finally {
    isReady.value = true;
    // Flip auth-resolved AFTER userStore is populated (or known-anon). Header
    // CTAs and similar auth-state UI gate on this flag to avoid flashing
    // anon→logged-in when an OAuth visitor returns to a public-marketing
    // route that renders before getUserData() resolves.
    userStore.isAuthResolved = true;
  }
};

const getBlockedEmailDomains = async () => {
  if (!userStore.isLogin()) return;
  await utilsStore.fetchBlockedEmailDomains();
}

// Only logged-in users need the blocked-email-domain list (profile button,
// org/register guards read it). Anonymous visitors on public pages don't — the
// sign-up form fetches it on demand (forceLoad). Watching `uuid` (instead of a
// one-shot call in `getUserData`'s finally) fires the prefetch whenever the
// session transitions to logged-in, including SPA logins (`router.push` after
// sign-in) that never re-run `getUserData`. `immediate` covers the full-reload
// path where `uuid` is already populated by the time this watch is set up.
watch(
  () => userStore.userV2.uuid,
  (uuid) => {
    if (uuid) getBlockedEmailDomains();
  },
  { immediate: true }
);

const userAuth = async (uuid: string) => {
  const isLoggedIn = uuid !== "";
  const routeName = getTopRouteName();
  const hasUrlQuery = Object.keys(route.query).length > 0;
  const isRegistered = parseBoolean(route.query.isRegistered, true);

  // 예외: 로그인이 필요없지만 필요한 경우
  // > 조직 초대
  if (!isLoggedIn && isOrgInvitationPath()) {
    if(!isRegistered) setRegisterContextCookie();
    await redirectToSignWithToast(isRegistered, SignType.ORGANIZATIONS);
    return;
  }

  // 예외: org 설정 페이지 (settings/org) 비로그인 접근 시 ORGANIZATIONS 타입으로 sign 이동
  if (!isLoggedIn && typeof route.name === 'string' && route.name.startsWith('settings-org')) {
    await redirectToSignWithToast(isRegistered, SignType.ORGANIZATIONS);
    return;
  }

  // 예외: assessment-id 접근 시 조직 가입 스킵
  if (!isLoggedIn && RouteName.LearningProSkillAssureAssessmentId === route.name) {
    if(!isRegistered) setRegisterContextCookie();
    await redirectToSignWithToast(isRegistered, SignType.CANDIDATE);
    return;
  }

  // 제한 없는 접근
  // renewal 콘텐츠로 서빙하는 로그인 이전 페이지는 이름 전체로 먼저 본다 —
  // 아래 `routeName` 은 getTopRouteName() 이 `-` 로 자른 값이라 `ax-build` 가 `ax` 가 된다.
  if (
    isRenewalRouteName(route.name) ||
    (unlimitedRouterName.value.includes(routeName) &&
      (routeName !== RouteName.Profile || (routeName === RouteName.Profile && hasUrlQuery)))
  ) {
    await stayHere();
    return;
  }

  // 로그인 상태: 불필요 라우트거나(예: sign/auth) / 일반유저가 매니저전용 접근이면 대시보드로
  if (isLoggedIn) {
    const isManagerOnly = isRouteNameOnlyManagerTypeCanAssess(route.name as ManagerRouteName);

    // 일반 유저가 매니저 전용 라우트 접근하는 케이스만 차단 (기존 로직 유지)
    const shouldGoDashboard =
      (isUnnecessaryLoginRouter(routeName) && !isOrgInvitationPath())
      || (!isManagerType.value && isManagerOnly);

    const canStayHere = !shouldGoDashboard;

    if (!guard(canStayHere, { redirectTo: { path: "/dashboard"} })) {
      return;
    }

    await stayHere();
    return;
  }

  // 비로그인: sign/auth 같은 곳은 그냥 둠
  if (isUnnecessaryLoginRouter(routeName)) {
    await stayHere();
    return;
  }

  // 비로그인: 나머지는 sign으로
  await redirectToSignWithToast(isRegistered, SignType.CANDIDATE);
};
</script>

<template>
  <div :key="locale">
    <!-- During the `getUserData()` round-trip on auth-required routes, this
         block is empty (white screen). Earlier attempts to show a spinner /
         skeleton here caused a double-loading-state on routes whose pages
         also open `loadingStore` on mount (e.g. dashboard, learning) — user
         saw skeleton → spinner → content, which felt worse than one quick
         white → content transition. -->
    <NuxtLayout v-if="isReady || isAnonMarketingRoute">
      <NuxtPage />
    </NuxtLayout>
    <!-- unique-tab: mounted once for every layout; pages opt in via
         definePageMeta({ sessionOwner: true }).
         :key is load-bearing — the guard binds its BroadcastChannel and localStorage key at
         setup, and v-if only toggles on true<->false, so navigating straight from one guarded
         route to another (practice 1 -> practice 2) would otherwise leave it claiming the
         first scope forever. Keying on the scope forces a remount. -->
    <SessionOwnerGuard
      v-if="route.meta.sessionOwner === true"
      :key="sessionScope"
      :scope="sessionScope"
    />
  </div>
</template>

<style>
@import "~/assets/scss/index.scss";
</style>
